import { CoreBetsGameCommunicationProcessor } from '@pp/core/src/controllers/gameCommunication/processors/CoreBetsGameCommunicationProcessor';
import { AcceptanceState, GameCommunicationMessageType } from '@pp/core/src/controllers/gameCommunication/types';
import { betActions } from '@pp/core/src/store/slices/bets/slice';
import { AcceptedBet, BetType } from '@pp/core/src/store/slices/bets/types';

import { autoCashoutActions } from '../../../store/slices/autocashout/slice';
import { betStepperActions } from '../../../store/slices/betStepper/slice';
import { highflyerStateActions } from '../../../store/slices/highflyerState/slice';

interface BetsMsg extends GameCommunicationMessageType {
  bet: {
    amount: string;
    betcode: string;
    mul: string;
  }[];
}

export class GameCommunicationHFBetsProcessor extends CoreBetsGameCommunicationProcessor {
  delay = 0;
  messageName = 'bets';
  /** gets invoked when user has placed a bet and reconnects to the game */
  process(message: BetsMsg): void {
    const tableId = this.store.getState().config.session?.tableId || '';
    const restoredBet: BetType = {
      betSpots: {},
    };

    if (!message.bet) {
      return;
    }
    const filteredAcceptedBets = message.bet.filter(({ mul }) => mul !== '-1');
    const acceptedBets: AcceptedBet[] = filteredAcceptedBets.map((bet) => {
      restoredBet.betSpots[bet.betcode] = { value: Number(bet.amount) };
      return {
        amount: Number(bet.amount),
        betCode: Number(bet.betcode),
        mul: bet.mul,
        errorCode: 0,
      };
    });

    const checkResult = this.gameCheckThatAllBetsAreProcessed(acceptedBets);
    if (acceptedBets.length > 0) {
      if (acceptedBets.length === 1) {
        this.store.dispatch(highflyerStateActions.setIsSingleBetPlaced(true));
      }
      acceptedBets.forEach(({ amount, betcode, mul, errorCode }) => {
        if (errorCode === 0) {
          this.store.dispatch(
            betStepperActions.setBetInputValue({
              betId: betcode,
              value: amount.toString(),
              isBetFinalized: true,
            })
          );
          this.store.dispatch(highflyerStateActions.setIsDealingInProgress(betcode));
          this.store.dispatch(
            autoCashoutActions.setCashoutMultiplier({
              betCode: betcode,
              cashoutMultiplier: Number(mul).toFixed(2),
              isAutoCashoutFinalized: true,
            })
          );
          this.store.dispatch(
            autoCashoutActions.setAutoCashoutButton({
              betCode: betcode,
              isEnabled: true,
            })
          );
          this.store.dispatch(highflyerStateActions.setIsBetPlaced(betcode));
        }
        if (errorCode === -1) {
          this.store.dispatch(betActions.wipeBet(betcode));
          this.store.dispatch(highflyerStateActions.resetIsBetPlaced(betcode));
        }
      });
    }
    if (checkResult === 'not_accepted' || checkResult === 'rejected') {
      this.store.dispatch(highflyerStateActions.setIsSingleBetPlaced(false));
    }

    this.store.dispatch(betActions.setAcceptedBets(acceptedBets));
    this.store.dispatch(betActions.updateBetStackHead(restoredBet));
    this.store.dispatch(betActions.saveLastBets({ tableId }));
  }

  private gameCheckThatAllBetsAreProcessed(acceptedBets: AcceptedBet[]): AcceptanceState {
    let rejectedBetsCount = 0;

    for (const bet of acceptedBets) {
      // error code -1 is set for bets that are yet to receive their resolve status
      if (bet.errorCode === -1) {
        return 'unknown';
      } else if (bet.errorCode > 0) {
        rejectedBetsCount++;
      }
    }

    if (rejectedBetsCount === acceptedBets.length) {
      return 'rejected';
    } else if (rejectedBetsCount > 0) {
      return 'not_accepted';
    }

    return 'accepted';
  }
}
